import { Slot as SlotPrimitive } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm leading-4 font-normal ring-offset-background transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border border-primary bg-primary text-primary-foreground shadow-[0px_2px_4px_primary] shadow-primary/10 hover:bg-primary/80 active:scale-[0.99] active:shadow-none',
        faded:
          'border-transparent bg-primary/10 text-primary hover:bg-primary/20',
        destructive:
          'border border-destructive text-destructive hover:bg-destructive/30',
        outline:
          'border border-input bg-surface shadow-[0px_2px_4px_rgba(85,85,85,0.04)] hover:bg-accent hover:text-secondary-foreground active:scale-[0.99]',
        secondary:
          'border border-secondary bg-secondary text-secondary-foreground hover:bg-secondary/60 dark:bg-secondary/80 dark:border-transparent dark:hover:bg-accent/60 dark:text-background',
        tertiary:
          'border border-accent bg-accent text-surface-foreground hover:bg-accent/60 dark:text-foreground',
        ghost: 'hover:bg-accent hover:text-foreground',
        link: 'text-link underline-offset-4 hover:underline',
      },
      size: {
        lg: 'h-11 rounded-md px-8',
        default: 'px-4 py-2',
        sm: 'rounded-md px-3 py-2',
        xs: 'rounded-md px-2 py-1',
        icon: 'p-1',
        'icon-sm': 'p-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? SlotPrimitive.Root : 'button';
  return (
    <Comp
      data-slot='button'
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
export { Button, buttonVariants, type ButtonProps };
